<template>
  <cal-dialog-shell persistent :initializing="initializing" :loading="loading" :save-disabled="!valid"
                    :value="showDialog" icon="mdi-check" save-icon="mdi-check" save-text="Erledigen" title="To-Do erledigen" tone="good"
                    @input="close" @save="complete">
    <v-form ref="form" v-model="valid" lazy-validation @submit.prevent="complete">
      <template v-if="item">
        <section class="mdlg-section">
          <div class="mdlg-section__title"><v-icon small>mdi-checkbox-marked-circle-outline</v-icon>To-Do</div>
          <div class="cdlg-panel complete-dialog__item">
            <div class="font-weight-medium complete-dialog__text">{{ item.name }}</div>
            <early-warning-to-do-details v-if="earlyWarning" :info="earlyWarning"/>
            <div v-else-if="item.description" class="acq-muted complete-dialog__text complete-dialog__text--multiline">{{ item.description }}</div>
            <span v-if="item.dueDate" :class="toDoDueTone(item)" class="tag tag--sm align-self-start">
              <v-icon class="tag__icon" x-small>{{ isToDoOverdue(item) ? 'mdi-alert-circle-outline' : 'mdi-calendar-clock' }}</v-icon>
              {{ isToDoOverdue(item) ? 'überfällig · ' : 'Fällig ' }}{{ formatToDoDue(item) }}
            </span>
          </div>
        </section>
        <section class="mdlg-section soft-inputs">
          <div class="mdlg-section__title"><v-icon small>mdi-message-reply-text-outline</v-icon>Rückmeldung</div>
          <v-switch v-if="item.dataType && item.dataType !== ToDoDataTypesEnum.NONE" v-model="sendWithoutData" color="info" dense
                    hide-details inset label="Ohne Daten absenden"/>
          <div v-if="sendWithoutData" class="mdlg-field">
            <span class="acq-label">Antwort</span>
            <v-text-field color="info" dense disabled hide-details outlined placeholder="Es werden keine Daten übermittelt"/>
          </div>
          <div v-else-if="item.dataType === ToDoDataTypesEnum.TEXT" class="mdlg-field">
            <span class="acq-label">Antwort (Text) <span class="mdlg-req">*</span></span>
            <v-textarea v-model.trim="data" :rules="[RuleFactory.required()]" color="info" dense hide-details="auto" no-resize outlined
                        rows="3"/>
          </div>
          <div v-else-if="item.dataType === ToDoDataTypesEnum.NUMBER" class="mdlg-field">
            <span class="acq-label">Antwort (Zahl) <span class="mdlg-req">*</span></span>
            <v-text-field v-model.number="data" :rules="[RuleFactory.required(), RuleFactory.isNumber()]" color="info" dense
                          hide-details="auto" outlined type="number"/>
          </div>
          <div v-else-if="item.dataType === ToDoDataTypesEnum.BOOLEAN" class="mdlg-field">
            <span class="acq-label">Antwort (Ja/Nein) <span class="mdlg-req">*</span></span>
            <v-select v-model.number="data" :items="boolItems" :rules="[RuleFactory.required()]" color="info" dense
                      item-color="info" outlined placeholder="Ja oder Nein wählen"/>
          </div>
          <div v-else-if="item.dataType === ToDoDataTypesEnum.MULTIPLE_CHOICE" class="mdlg-field">
            <span class="acq-label">Antwort (Auswahl) <span class="mdlg-req">*</span></span>
            <v-select v-model.number="data" :items="item.dataMultipleChoiceOptions || []" :rules="[RuleFactory.required()]" color="info"
                      dense hide-details="auto" item-color="info" outlined placeholder="Option wählen"/>
          </div>
          <div v-else-if="item.dataType === ToDoDataTypesEnum.DATE" class="mdlg-field">
            <span class="acq-label">Antwort (Datum) <span class="mdlg-req">*</span></span>
            <date-picker v-model.number="data" :rounded="false" :rules="[RuleFactory.required()]" color="info" dense
                         outlined/>
          </div>
          <div v-if="sendWithoutData" class="mdlg-field">
            <span class="acq-label">Kommentar <span class="mdlg-req">*</span></span>
            <v-textarea v-model="comment" :rules="[RuleFactory.required('Wenn keine Antwortdaten übermittelt werden ist ein Kommentar erforderlich')]"
                        color="info" counter dense hide-details="auto" no-resize outlined rows="3"/>
            <div class="acq-tiny">Ohne Antwortdaten ist ein Kommentar erforderlich.</div>
          </div>
          <div v-else-if="item.dataType !== ToDoDataTypesEnum.TEXT" class="mdlg-field">
            <span class="acq-label">Kommentar (optional)</span>
            <v-textarea v-model="comment" color="info" counter dense hide-details="auto" no-resize outlined rows="3"/>
          </div>
        </section>
        <debug-card :value="completeDto"/>
      </template>
    </v-form>
  </cal-dialog-shell>
</template>

<script lang="ts">
import Vue from 'vue';
import CalDialogShell from '@/components/timetable2/dialogs/CalDialogShell.vue';
import {RuleFactory} from '@/helpers/ruleFactory.helper';
import {Form} from '@/interfaces/Form.interface';
import mongoose, {LeanDocument} from 'mongoose';
import {ToDoItemDocument} from '@/documentTypes/to-do-manager/ToDoItem.document';
import {ToDoManagerApi} from '@/classes/api/to-do-manager.api.class';
import {ToDoDataTypesEnum} from '@/enums/to-do-manager/ToDoDataTypes.enum';
import DatePicker from '@/components/common/datePicker.vue';
import {CompleteToDoItemRequestDto} from '@/classes/dto/to-do-manager/request/CompleteToDoItem.request.dto';
import DebugCard from '@/components/common/debugCard.vue';
import {formatToDoDue, isToDoOverdue, toDoDueTone} from '@/helpers/toDoDue.helper';
import EarlyWarningToDoDetails from '@/components/toDos/earlyWarningToDoDetails.vue';
import {EarlyWarningToDoInfo, earlyWarningToDoInfo} from '@/components/toDos/earlyWarningToDo.helper';

export default Vue.extend({
  components: {CalDialogShell, DebugCard, DatePicker, EarlyWarningToDoDetails},
  data: () => ({
    initializing: false,
    loading: false,
    valid: false,
    itemId: undefined as undefined | mongoose.Types.ObjectId,
    item: undefined as LeanDocument<ToDoItemDocument> | undefined,

    resolve: null as null | ((value: unknown) => void),
    reject: null as null | ((value: unknown) => void),

    showDialog: false,

    sendWithoutData: false,

    data: undefined as any,
    comment: '',

    boolItems: [
      {value: true, text: 'Ja'},
      {value: false, text: 'Nein'},
    ] as Array<{ value: boolean, text: string }>,

  }),
  computed: {
    ToDoDataTypesEnum: () => ToDoDataTypesEnum,
    RuleFactory: () => RuleFactory,
    earlyWarning(): EarlyWarningToDoInfo | null {
      return earlyWarningToDoInfo(this.item);
    },
    completeDto(): CompleteToDoItemRequestDto {
      return {
        comment: this.comment.length > 0 ? this.comment : undefined,
        data: this.sendWithoutData ? undefined : this.data,
        value: true,
      };
    },
  },
  methods: {
    isToDoOverdue,
    toDoDueTone,
    formatToDoDue,
    open(itemId: mongoose.Types.ObjectId) {
      this.itemId = itemId;
      return new Promise((resolve, reject) => {
        this.showDialog = true;
        this.resolve = resolve;
        this.reject = reject;
      });
    },
    async complete() {
      if (!this.itemId) return;
      this.loading = true;
      try {
        await ToDoManagerApi.items.complete(this.itemId, this.completeDto);
        if (this.resolve) {
          this.resolve(true);
        }
        this.close();
      } catch (e) {
        this.$$showSnackbar('Fehler beim Erledigen des To-Dos', 'error', e);
      } finally {
        this.loading = false;
      }
    },
    async initialize() {
      if (!this.itemId) return;
      this.initializing = true;
      this.data = undefined;
      try {
        this.item = await ToDoManagerApi.items.findById(this.itemId);
        this.comment = this.item.completionComment || '';
      } catch (e) {
        this.$$showSnackbar('Fehler beim Initialisieren der Komponente', 'error', e);
      } finally {
        this.initializing = false;
      }
    },
    /** Explicit defaults instead of form.reset() (that sets the fields to null) */
    close() {
      (this.$refs.form as Form | undefined)?.resetValidation();
      this.sendWithoutData = false;
      this.data = undefined;
      if (this.reject) {
        this.reject(false);
      }
      this.reject = null;
      this.resolve = null;
      this.comment = '';
      this.showDialog = false;
    },
  },
  watch: {
    itemId: {
      immediate: true,
      handler(v: boolean) {
        if (v) {
          this.initialize();
        }
      },
    },
  },
})
</script>

<style scoped>
.complete-dialog__item {
  gap: 6px;
}

.complete-dialog__text {
  word-break: normal;
  overflow-wrap: anywhere;
  white-space: normal;
}

/* Descriptions may have several lines (e.g. to-dos of the absence early warning) */
.complete-dialog__text--multiline {
  white-space: pre-line;
}
</style>
